import { buildStats, htmlToMarkdown, markdownToHtml } from "./services/converter";
import { openMarkdownFile, saveMarkdownFile } from "./services/fileService";
import { downloadRecentLogs, log } from "./services/logger";
import { OfficeWordGateway } from "./services/wordGateway";
import type { FileReference, WordDocumentGateway } from "./types/contracts";

export const STORAGE_KEY = "markdown-word-addin.currentDocument";

export type Elements = {
  editor: HTMLTextAreaElement;
  preview: HTMLDivElement;
  openBtn: HTMLButtonElement;
  saveBtn: HTMLButtonElement;
  pullBtn: HTMLButtonElement;
  pushBtn: HTMLButtonElement;
  downloadLogsBtn: HTMLButtonElement;
  fileInfo: HTMLDivElement;
  stats: HTMLDivElement;
  status: HTMLParagraphElement;
};

type LogLevel = "debug" | "info" | "warn" | "error";

type AppDependencies = {
  buildStats: (markdown: string) => { words: number; chars: number };
  markdownToHtml: (markdown: string) => string;
  htmlToMarkdown: (html: string) => string;
  openMarkdownFile: () => Promise<{ content: string; file: FileReference } | null>;
  saveMarkdownFile: (
    content: string,
    preferredName: string,
    currentFile?: FileReference,
  ) => Promise<FileReference>;
  downloadRecentLogs: () => void;
  log: (level: LogLevel, event: string, details?: Record<string, unknown>) => void;
  storage: Storage;
  wordGateway: WordDocumentGateway;
};

function isAbortLikeError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function createDefaultDependencies(storage: Storage = localStorage): AppDependencies {
  return {
    buildStats,
    markdownToHtml,
    htmlToMarkdown,
    openMarkdownFile,
    saveMarkdownFile,
    downloadRecentLogs,
    log,
    storage,
    wordGateway: new OfficeWordGateway(),
  };
}

export class AppController {
  private currentFile?: FileReference;

  constructor(
    private readonly elements: Elements,
    private readonly dependencies: AppDependencies,
  ) {
    this.elements.editor.addEventListener("input", () => this.onMarkdownChanged());
    this.elements.openBtn.addEventListener("click", () => void this.openFile());
    this.elements.saveBtn.addEventListener("click", () => void this.saveFile());
    this.elements.pullBtn.addEventListener("click", () => void this.pullFromWord());
    this.elements.pushBtn.addEventListener("click", () => void this.pushToWord());
    this.elements.downloadLogsBtn.addEventListener("click", () => this.downloadLogs());
  }

  initialize(): void {
    this.dependencies.log("info", "app.initialize.start");
    const saved = this.dependencies.storage.getItem(STORAGE_KEY);
    if (saved) {
      this.elements.editor.value = saved;
      this.renderMarkdown(saved);
      this.setStatus("Restored previous draft from local storage.");
      this.dependencies.log("info", "app.initialize.restoredDraft", { chars: saved.length });
      return;
    }

    this.elements.editor.value = "# Welcome\n\nStart typing Markdown or open an existing `.md` file.";
    this.renderMarkdown(this.elements.editor.value);
    this.dependencies.log("info", "app.initialize.defaultDraft");
  }

  private onMarkdownChanged(): void {
    const markdown = this.elements.editor.value;
    this.dependencies.storage.setItem(STORAGE_KEY, markdown);
    this.renderMarkdown(markdown);
  }

  private renderMarkdown(markdown: string): void {
    this.elements.preview.innerHTML = this.dependencies.markdownToHtml(markdown);
    const stats = this.dependencies.buildStats(markdown);
    this.elements.stats.textContent = `${stats.words} words, ${stats.chars} chars`;
    this.elements.fileInfo.textContent = this.currentFile?.name ?? "Unsaved markdown";
  }

  private async openFile(): Promise<void> {
    this.dependencies.log("info", "action.openFile.start");
    try {
      const opened = await this.dependencies.openMarkdownFile();
      if (!opened) {
        this.setStatus("Open cancelled.");
        this.dependencies.log("info", "action.openFile.cancelled");
        return;
      }

      this.currentFile = opened.file;
      this.elements.editor.value = opened.content;
      this.onMarkdownChanged();
      this.setStatus(`Opened ${opened.file.name}.`);
      this.dependencies.log("info", "action.openFile.success", {
        filename: opened.file.name,
        chars: opened.content.length,
      });
    } catch (error) {
      if (isAbortLikeError(error)) {
        this.dependencies.log("info", "action.openFile.cancelled.abort");
        return;
      }
      this.setStatus(`Failed to open Markdown: ${String(error)}`, true);
      this.dependencies.log("error", "action.openFile.error", { error });
    }
  }

  private async saveFile(): Promise<void> {
    this.dependencies.log("info", "action.saveFile.start", { currentFile: this.currentFile?.name });
    try {
      const name = this.currentFile?.name ?? "document.md";
      this.currentFile = await this.dependencies.saveMarkdownFile(this.elements.editor.value, name, this.currentFile);
      this.renderMarkdown(this.elements.editor.value);
      this.setStatus(`Saved ${this.currentFile.name}.`);
      this.dependencies.log("info", "action.saveFile.success", { filename: this.currentFile.name });
    } catch (error) {
      if (isAbortLikeError(error)) {
        this.dependencies.log("info", "action.saveFile.cancelled.abort");
        return;
      }
      this.setStatus(`Failed to save Markdown: ${String(error)}`, true);
      this.dependencies.log("error", "action.saveFile.error", { error });
    }
  }

  private async pullFromWord(): Promise<void> {
    this.dependencies.log("info", "action.pullFromWord.start");
    try {
      this.setBusy(true);
      const html = await this.dependencies.wordGateway.readBodyHtml();
      const markdown = this.dependencies.htmlToMarkdown(html);
      this.elements.editor.value = markdown;
      this.onMarkdownChanged();
      this.setStatus("Pulled content from Word document.");
      this.dependencies.log("info", "action.pullFromWord.success", {
        htmlChars: html.length,
        markdownChars: markdown.length,
      });
    } catch (error) {
      this.setStatus(`Failed to pull from Word: ${String(error)}`, true);
      this.dependencies.log("error", "action.pullFromWord.error", { error });
    } finally {
      this.setBusy(false);
    }
  }

  private async pushToWord(): Promise<void> {
    this.dependencies.log("info", "action.pushToWord.start");
    try {
      this.setBusy(true);
      const html = this.dependencies.markdownToHtml(this.elements.editor.value);
      await this.dependencies.wordGateway.replaceBodyFromHtml(html);
      this.setStatus("Pushed Markdown to Word document.");
      this.dependencies.log("info", "action.pushToWord.success", {
        markdownChars: this.elements.editor.value.length,
        htmlChars: html.length,
      });
    } catch (error) {
      this.setStatus(`Failed to push to Word: ${String(error)}`, true);
      this.dependencies.log("error", "action.pushToWord.error", { error });
    } finally {
      this.setBusy(false);
    }
  }

  private setBusy(isBusy: boolean): void {
    const controls = [
      this.elements.openBtn,
      this.elements.saveBtn,
      this.elements.pullBtn,
      this.elements.pushBtn,
      this.elements.editor,
    ];

    for (const control of controls) {
      control.disabled = isBusy;
    }
  }

  private setStatus(message: string, isError = false): void {
    this.elements.status.textContent = message;
    this.elements.status.dataset.state = isError ? "error" : "ok";
  }

  private downloadLogs(): void {
    this.dependencies.downloadRecentLogs();
    this.setStatus("Diagnostics downloaded.");
    this.dependencies.log("info", "action.downloadLogs.success");
  }
}

export function getElements(): Elements {
  const editor = document.getElementById("markdownEditor");
  const preview = document.getElementById("preview");
  const openBtn = document.getElementById("openFileBtn");
  const saveBtn = document.getElementById("saveFileBtn");
  const pullBtn = document.getElementById("pullWordBtn");
  const pushBtn = document.getElementById("pushWordBtn");
  const downloadLogsBtn = document.getElementById("downloadLogsBtn");
  const fileInfo = document.getElementById("fileInfo");
  const stats = document.getElementById("stats");
  const status = document.getElementById("status");

  if (
    !(editor instanceof HTMLTextAreaElement) ||
    !(preview instanceof HTMLDivElement) ||
    !(openBtn instanceof HTMLButtonElement) ||
    !(saveBtn instanceof HTMLButtonElement) ||
    !(pullBtn instanceof HTMLButtonElement) ||
    !(pushBtn instanceof HTMLButtonElement) ||
    !(downloadLogsBtn instanceof HTMLButtonElement) ||
    !(fileInfo instanceof HTMLDivElement) ||
    !(stats instanceof HTMLDivElement) ||
    !(status instanceof HTMLParagraphElement)
  ) {
    throw new Error("Failed to bind UI elements.");
  }

  return { editor, preview, openBtn, saveBtn, pullBtn, pushBtn, downloadLogsBtn, fileInfo, stats, status };
}
